import { useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';

const CLEAN_UP_TIMEOUT_MS = 10_000;

export const useNavigateToHash = () => {
  const { hash } = useLocation();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const observerRef = useRef<MutationObserver | undefined>(undefined);

  useEffect(() => {
    // Clean up all timeout and observer when hash is changed
    clearTimeout(timeoutIdRef.current);
    observerRef.current?.disconnect();

    if (!hash) return;

    const id = hash.replace('#', '');

    const scrollToElement = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
      }
      return false;
    };

    // Try to scroll immediately
    if (scrollToElement()) return;

    // Observe DOM changes if element not yet present
    observerRef.current = new MutationObserver(() => {
      if (scrollToElement()) {
        observerRef.current?.disconnect(); // stop observing once found
      }
    });

    observerRef.current.observe(document.body, { childList: true, subtree: true });

    // Auto cleanup after 10 seconds
    // TODO make customizable via params?
    timeoutIdRef.current = setTimeout(() => observerRef.current?.disconnect(), CLEAN_UP_TIMEOUT_MS);

    // Cleanup on unmount or hash change
    return () => {
      observerRef.current?.disconnect();
      clearTimeout(timeoutIdRef.current);
    };
  }, [hash]);
};
